import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiSend, FiSearch, FiMessageCircle, FiTrash2, FiEye, FiUsers, FiLock } from 'react-icons/fi';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const avatar = (name = '?') => name.charAt(0).toUpperCase();
const fmt = (d) => {
  const dt = new Date(d);
  const now = new Date();
  if (dt.toDateString() === now.toDateString()) {
    return dt.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });
  }
  return dt.toLocaleDateString('en-IN', { day:'numeric', month:'short' });
};
const fmtFull = (d) => new Date(d).toLocaleString('en-IN', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });

export default function Messages() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initUserId = searchParams.get('with') ? parseInt(searchParams.get('with')) : null;

  const [conversations, setConversations] = useState([]);
  const [users,         setUsers]         = useState([]);
  const [activeUser,    setActiveUser]    = useState(null);
  const [messages,      setMessages]      = useState([]);
  const [body,          setBody]          = useState('');
  const [search,        setSearch]        = useState('');
  const [newSearch,     setNewSearch]     = useState('');
  const [showNewChat,   setShowNewChat]   = useState(false);
  const [loadingMsgs,   setLoadingMsgs]   = useState(false);
  // Admin mode
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [adminMode,      setAdminMode]      = useState(false);
  const [allConvos,      setAllConvos]      = useState([]);
  const [adminSearch,    setAdminSearch]    = useState('');
  const [adminThread,    setAdminThread]    = useState(null); // {uid1, uid2, name1, name2}
  const messagesEndRef = useRef(null);
  const pollRef        = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/messages/conversations`, { headers: headers() });
      setConversations(r.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadMessages = useCallback(async (otherId) => {
    if (!otherId) return;
    try {
      setLoadingMsgs(true);
      const r = await axios.get(`${API}/messages/${otherId}`, { headers: headers() });
      setMessages(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingMsgs(false); }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/messages/users/list`, { headers: headers() });
      setUsers(r.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadAllConvos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/messages/admin/all`, { headers: headers() });
      setAllConvos(r.data.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadAdminThread = useCallback(async (uid1, uid2) => {
    try {
      setLoadingMsgs(true);
      const r = await axios.get(`${API}/messages/admin/thread/${uid1}/${uid2}`, { headers: headers() });
      setMessages(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingMsgs(false); }
  }, []);

  useEffect(() => { loadConvs(); loadUsers(); }, [loadConvs, loadUsers]);

  useEffect(() => {
    if (adminMode && isAdmin) { loadAllConvos(); setActiveUser(null); setAdminThread(null); setMessages([]); }
  // eslint-disable-next-line
  }, [adminMode]);

  useEffect(() => {
    if (adminThread) loadAdminThread(adminThread.uid1, adminThread.uid2);
  // eslint-disable-next-line
  }, [adminThread]);

  useEffect(() => {
    if (initUserId && users.length) {
      const u = users.find(u => u.id === initUserId);
      if (u) openChat(u);
    }
  // eslint-disable-next-line
  }, [initUserId, users]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!activeUser) return;
    pollRef.current = setInterval(() => {
      loadMessages(activeUser.id);
      loadConvs();
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeUser, loadMessages, loadConvs]);

  const openChat = (u) => {
    setActiveUser(u);
    setShowNewChat(false);
    setSearchParams({ with: u.id });
    loadMessages(u.id);
    loadConvs();
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!body.trim() || !activeUser) return;
    try {
      await axios.post(`${API}/messages`, { receiverId: activeUser.id, body: body.trim() }, { headers: headers() });
      setBody('');
      loadMessages(activeUser.id);
      loadConvs();
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };

  const deleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/messages/${id}`, { headers: headers() });
      setMessages(ms => ms.filter(m => m.id !== id));
    } catch (e) { alert(e.response?.data?.message || 'Error'); }
  };

  const filteredConvs = conversations.filter(c =>
    c.user?.name?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(newSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(newSearch.toLowerCase())
  );

  const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);

  return (
    <div style={{ display:'flex', height:'calc(100vh - 64px)', background:'#f8fafc', overflow:'hidden', fontFamily:"'Sora',sans-serif" }}>

      {/* Sidebar */}
      <div style={{ width:300, borderRight:'1px solid #e2e8f0', display:'flex', flexDirection:'column', flexShrink:0, background:'#fff' }}>
        {/* Header */}
        <div style={{ padding:'20px 16px 12px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:17, fontWeight:700, color:'#0f172a' }}>
              {adminMode ? '👁 All Conversations' : <>Messages {totalUnread > 0 && (
                <span style={{ background:'var(--pm-primary)', color:'#fff', borderRadius:10, fontSize:11, padding:'1px 7px', marginLeft:6 }}>
                  {totalUnread}
                </span>
              )}</>}
            </div>
            <div style={{ display:'flex', gap:6 }}>
              {isAdmin && (
                <button onClick={() => setAdminMode(m => !m)}
                  title={adminMode ? 'Back to my messages' : 'View all conversations'}
                  style={{ background: adminMode ? '#fef3c7' : '#f1f5f9', border:'none', borderRadius:8, color: adminMode ? '#d97706' : '#64748b', padding:'5px 9px', cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:4 }}>
                  <FiUsers size={13}/> {adminMode ? 'Mine' : 'All'}
                </button>
              )}
              {!adminMode && (
                <button onClick={() => setShowNewChat(s => !s)}
                  style={{ background:'var(--pm-gradient)', border:'none', borderRadius:8, color:'#fff', padding:'5px 12px', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                  + New
                </button>
              )}
            </div>
          </div>
          <div style={{ position:'relative' }}>
            <FiSearch style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#94a3b8', fontSize:14 }} />
            <input style={{ width:'100%', padding:'8px 10px 8px 32px', background:'#f8fafc', border:'1.5px solid #e2e8f0', borderRadius:8, color:'#0f172a', fontSize:13, outline:'none', boxSizing:'border-box' }}
              placeholder={adminMode ? 'Search all conversations...' : 'Search conversations...'}
              value={adminMode ? adminSearch : search}
              onChange={e => adminMode ? setAdminSearch(e.target.value) : setSearch(e.target.value)} />
          </div>
        </div>

        {/* New chat panel */}
        {showNewChat && (
          <div style={{ borderBottom:'1px solid #f1f5f9', maxHeight:220, overflowY:'auto', background:'#fafbfc' }}>
            <div style={{ padding:'8px 12px', fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em' }}>Start New Chat</div>
            <div style={{ padding:'0 12px 8px' }}>
              <input style={{ width:'100%', padding:'7px 10px', background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:8, color:'#0f172a', fontSize:13, outline:'none', boxSizing:'border-box' }}
                placeholder="Search people..." value={newSearch} onChange={e => setNewSearch(e.target.value)} />
            </div>
            {filteredUsers.map(u => (
              <div key={u.id}
                onClick={() => openChat(u)}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 16px', cursor:'pointer', transition:'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--pm-gradient)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, color:'#fff', flexShrink:0 }}>
                  {avatar(u.name)}
                </div>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0f172a' }}>{u.name}</div>
                  <div style={{ fontSize:11, color:'#94a3b8', textTransform:'capitalize' }}>{u.role} · {u.position || u.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Conversation list */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {/* Admin: all conversations view */}
          {adminMode && (() => {
            const filtered = allConvos.filter(c => {
              const q = adminSearch.toLowerCase();
              return !q || c.user1?.name?.toLowerCase().includes(q) || c.user2?.name?.toLowerCase().includes(q);
            });
            if (filtered.length === 0) return (
              <div style={{ padding:24, textAlign:'center', color:'#94a3b8', fontSize:13 }}>
                No conversations found.
              </div>
            );
            return filtered.map((c, i) => {
              const isActive = adminThread?.uid1 === c.user1?.id && adminThread?.uid2 === c.user2?.id;
              return (
                <div key={i} onClick={() => { setAdminThread({ uid1: c.user1?.id, uid2: c.user2?.id, name1: c.user1?.name, name2: c.user2?.name }); setActiveUser(null); }}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', cursor:'pointer',
                    background: isActive ? '#fef3c7' : 'transparent',
                    borderLeft: isActive ? '3px solid #d97706' : '3px solid transparent', transition:'all 0.15s' }}>
                  <div style={{ width:38, height:38, borderRadius:'50%', background:'linear-gradient(135deg,#f59e0b,#d97706)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, color:'#fff', flexShrink:0 }}>
                    <FiUsers size={15}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {c.user1?.name} ↔ {c.user2?.name}
                    </div>
                    <div style={{ fontSize:11, color:'#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:2 }}>
                      {c.lastMessage}
                    </div>
                  </div>
                  <div style={{ fontSize:10, color:'#94a3b8', flexShrink:0 }}>{fmt(c.lastAt)}</div>
                </div>
              );
            });
          })()}

          {/* Regular view */}
          {!adminMode && filteredConvs.length === 0 && !showNewChat && (
            <div style={{ padding:24, textAlign:'center', color:'#94a3b8', fontSize:13 }}>
              No conversations yet.<br />Click "+ New" to start one.
            </div>
          )}
          {!adminMode && filteredConvs.map(conv => (
            <div key={conv.user?.id}
              onClick={() => openChat(conv.user)}
              style={{
                display:'flex', alignItems:'center', gap:10, padding:'12px 16px', cursor:'pointer',
                background: activeUser?.id === conv.user?.id ? '#f5f3ff' : 'transparent',
                borderLeft: activeUser?.id === conv.user?.id ? '3px solid var(--pm-primary)' : '3px solid transparent',
                transition:'all 0.15s',
              }}>
              <div style={{ position:'relative', flexShrink:0 }}>
                <div style={{ width:38, height:38, borderRadius:'50%', background:'var(--pm-gradient)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:15, color:'#fff' }}>
                  {avatar(conv.user?.name)}
                </div>
                {conv.unread > 0 && (
                  <div style={{ position:'absolute', top:-2, right:-2, width:16, height:16, background:'var(--pm-primary)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, color:'#fff', fontWeight:700 }}>
                    {conv.unread}
                  </div>
                )}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0f172a', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {conv.user?.name}
                  </div>
                  <div style={{ fontSize:11, color:'#94a3b8', flexShrink:0 }}>{fmt(conv.lastAt)}</div>
                </div>
                <div style={{ fontSize:12, color:'#64748b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:2 }}>
                  {conv.lastMessage}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f8fafc' }}>
        {/* Admin thread view */}
        {adminMode && adminThread ? (
          <>
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #e2e8f0', display:'flex', alignItems:'center', gap:12, background:'#fffbeb', boxShadow:'0 1px 4px #0000000a' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'linear-gradient(135deg,#f59e0b,#d97706)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', flexShrink:0 }}>
                <FiUsers size={18}/>
              </div>
              <div>
                <div style={{ fontWeight:700, color:'#0f172a', fontSize:15 }}>{adminThread.name1} ↔ {adminThread.name2}</div>
                <div style={{ fontSize:12, color:'#d97706', display:'flex', alignItems:'center', gap:4 }}><FiLock size={11}/> Admin view — read only</div>
              </div>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:4 }}>
              {loadingMsgs && <div style={{ textAlign:'center', color:'#94a3b8', padding:20 }}>Loading...</div>}
              {!loadingMsgs && messages.length === 0 && (
                <div style={{ textAlign:'center', color:'#94a3b8', padding:40 }}>No messages in this conversation.</div>
              )}
              {messages.map((m, i) => {
                const senderName = m.sender?.name || (m.senderId === adminThread.uid1 ? adminThread.name1 : adminThread.name2);
                const isUser1 = m.senderId === adminThread.uid1;
                const showDate = i === 0 || new Date(m.createdAt).toDateString() !== new Date(messages[i-1]?.createdAt).toDateString();
                return (
                  <React.Fragment key={m.id}>
                    {showDate && (
                      <div style={{ textAlign:'center', margin:'12px 0 4px', fontSize:11, color:'#94a3b8' }}>
                        {new Date(m.createdAt).toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}
                      </div>
                    )}
                    <div style={{ display:'flex', justifyContent: isUser1 ? 'flex-start' : 'flex-end', marginBottom:2 }}>
                      <div style={{ maxWidth:'65%' }}>
                        <div style={{ fontSize:10, color:'#94a3b8', marginBottom:2, textAlign: isUser1 ? 'left' : 'right', fontWeight:600 }}>{senderName}</div>
                        <div style={{
                          background: isUser1 ? '#fff' : '#f0fdf4',
                          color:'#0f172a', padding:'9px 14px',
                          borderRadius: isUser1 ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                          fontSize:13, lineHeight:1.5,
                          border: isUser1 ? '1px solid #e2e8f0' : '1px solid #bbf7d0',
                          boxShadow:'0 1px 4px #00000008', wordBreak:'break-word',
                        }}>{m.body}</div>
                        <div style={{ fontSize:10, color:'#94a3b8', marginTop:2, textAlign: isUser1 ? 'left' : 'right' }} title={fmtFull(m.createdAt)}>{fmt(m.createdAt)}</div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ padding:'10px 20px', borderTop:'1px solid #e2e8f0', background:'#fffbeb', textAlign:'center', fontSize:12, color:'#d97706', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <FiLock size={12}/> You are viewing this conversation as an admin. You cannot send messages here.
            </div>
          </>
        ) : adminMode && !adminThread ? (
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#94a3b8' }}>
            <div style={{ width:80, height:80, borderRadius:'50%', background:'#fef3c7', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
              <FiUsers style={{ fontSize:36, color:'#d97706' }} />
            </div>
            <div style={{ fontSize:16, fontWeight:600, color:'#64748b' }}>Select a conversation to view</div>
            <div style={{ fontSize:13, marginTop:6, color:'#94a3b8' }}>Showing all conversations between team members & clients</div>
          </div>
        ) : !activeUser ? (
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#94a3b8' }}>
            <div style={{ width:80, height:80, borderRadius:'50%', background:'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
              <FiMessageCircle style={{ fontSize:36, color:'#cbd5e1' }} />
            </div>
            <div style={{ fontSize:16, fontWeight:600, color:'#64748b' }}>Select a conversation</div>
            <div style={{ fontSize:13, marginTop:6, color:'#94a3b8' }}>or click "+ New" to start one</div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div style={{ padding:'14px 20px', borderBottom:'1px solid #e2e8f0', display:'flex', alignItems:'center', gap:12, background:'#fff', boxShadow:'0 1px 4px #0000000a' }}>
              <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--pm-gradient)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:16, color:'#fff', flexShrink:0 }}>
                {avatar(activeUser.name)}
              </div>
              <div>
                <div style={{ fontWeight:700, color:'#0f172a', fontSize:15 }}>{activeUser.name}</div>
                <div style={{ fontSize:12, color:'#64748b', textTransform:'capitalize' }}>{activeUser.role}{activeUser.position ? ` · ${activeUser.position}` : ''}</div>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex:1, overflowY:'auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:4 }}>
              {loadingMsgs && <div style={{ textAlign:'center', color:'#94a3b8', padding:20 }}>Loading...</div>}
              {!loadingMsgs && messages.length === 0 && (
                <div style={{ textAlign:'center', color:'#94a3b8', padding:40 }}>
                  No messages yet. Say hi! 👋
                </div>
              )}
              {messages.map((m, i) => {
                const isMe = m.senderId === user.id;
                const showDate = i === 0 || new Date(m.createdAt).toDateString() !== new Date(messages[i-1]?.createdAt).toDateString();
                return (
                  <React.Fragment key={m.id}>
                    {showDate && (
                      <div style={{ textAlign:'center', margin:'12px 0 4px', fontSize:11, color:'#94a3b8' }}>
                        {new Date(m.createdAt).toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}
                      </div>
                    )}
                    <div style={{ display:'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom:2 }}>
                      <div style={{ position:'relative', maxWidth:'65%' }}>
                        <div style={{
                          background: isMe ? 'var(--pm-gradient)' : '#fff',
                          color: isMe ? '#fff' : '#0f172a',
                          padding:'9px 14px',
                          borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          fontSize:13, lineHeight:1.5,
                          border: isMe ? 'none' : '1px solid #e2e8f0',
                          boxShadow: '0 1px 4px #00000008',
                          wordBreak:'break-word',
                        }}>
                          {m.body}
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:6, justifyContent: isMe ? 'flex-end' : 'flex-start', marginTop:2 }}>
                          <span style={{ fontSize:10, color:'#94a3b8' }} title={fmtFull(m.createdAt)}>{fmt(m.createdAt)}</span>
                          {isMe && m.isRead && <span style={{ fontSize:10, color:'var(--pm-primary)' }}>✓✓</span>}
                          {isMe && !m.isRead && <span style={{ fontSize:10, color:'#94a3b8' }}>✓</span>}
                          {isMe && (
                            <button onClick={() => deleteMessage(m.id)}
                              style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:0, fontSize:11 }}
                              title="Delete">
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input bar */}
            <div style={{ padding:'12px 20px', borderTop:'1px solid #e2e8f0', background:'#fff' }}>
              <form onSubmit={sendMessage} style={{ display:'flex', gap:10, alignItems:'center' }}>
                <input
                  style={{ flex:1, padding:'11px 18px', background:'#f8fafc', border:'1.5px solid #e2e8f0', borderRadius:24, color:'#0f172a', fontSize:13, outline:'none', transition:'border .2s' }}
                  placeholder={`Message ${activeUser.name}...`}
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  onFocus={e => e.target.style.borderColor='var(--pm-primary)'}
                  onBlur={e => e.target.style.borderColor='#e2e8f0'}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { sendMessage(e); } }}
                />
                <button type="submit" disabled={!body.trim()}
                  style={{ width:42, height:42, borderRadius:'50%', background: body.trim() ? 'var(--pm-gradient)' : '#e2e8f0', border:'none', cursor: body.trim() ? 'pointer' : 'default', color: body.trim() ? '#fff' : '#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.2s', flexShrink:0 }}>
                  <FiSend style={{ fontSize:16 }} />
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
